import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Row, Col, message, Breadcrumb, Spin, Empty, Tabs, Badge, Space, Modal, Button, Pagination } from 'antd';
import { BookOutlined, HomeOutlined, ReadOutlined, RightOutlined, LeftOutlined, FullscreenOutlined, FullscreenExitOutlined, FileTextOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import axios from '../utils/axios';
import styles from './StudyBook.module.css';
import api from '../utils/api';
// 导入二维码图片
import qrCodeImage from '../assets/xcx.jpg';

const { Title, Text, Paragraph } = Typography;

const StudyBook = () => {
  const location = useLocation();
  const { subCategoryCode } = useParams();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState('0'); // 默认选中第一本书
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [pageSize] = useState(30); // 每页显示数量
  const [currentPage, setCurrentPage] = useState(1); // 当前页码
  const [isFullscreen, setIsFullscreen] = useState(false);

  const categoryInfo = location.state || {};

  console.log('Params:', { subCategoryCode, categoryInfo }); // 调试用

  // 打开知识笔记页面
  const openNotesPage = () => {
    window.open(`/study-book/${subCategoryCode}`, '_blank');
  };

  useEffect(() => {
    console.log('Component mounted with:', {
      subCategoryCode,
      categoryInfo,
      locationState: location.state,
      currentBooks: books
    });
  }, []);

  const fetchKnowledgePoints = async () => {
    try {
      setLoading(true);
      console.log('Fetching knowledge points for:', subCategoryCode);

      const response = await api.get(`/study/knowledge-category/${subCategoryCode}`);
      console.log('API Response:', response.data);

      if (response.data.success) {
        const booksData = response.data.books || [];
        console.log('Books data:', booksData);

        if (booksData.length === 0) {
          console.log('No books data received');
          message.info('暂无知识点数据');
        }

        const processedBooks = booksData.map(book => ({
          book_title: book.book_title,
          knowledge_points: Array.isArray(book.knowledge_points) ?
            book.knowledge_points.map(point => ({
              _id: point._id,
              point_id: point.point_id,
              description: point.description,
              description_more: point.description_more || ''
            })) : []
        }));

        console.log('Setting processed books:', processedBooks);
        setBooks(processedBooks);
      } else {
        console.error('API returned error:', response.data.message);
        message.error(response.data.message || '获取数据失败');
      }
    } catch (error) {
      console.error('获取知识点失败:', error);
      message.error('获取知识点数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (subCategoryCode) {
      console.log('Triggering fetch for subCategoryCode:', subCategoryCode);
      fetchKnowledgePoints();
    } else {
      console.log('No subCategoryCode available');
    }
  }, [subCategoryCode]);

  console.log('Rendering with state:', {
    loading,
    booksLength: books.length,
    categoryInfo
  });

  const getKnowledgeInfo = async (pointId) => {
    const res = await api.get(`/study/knowledge-info/${pointId}`)
    return res.data.data
  }

  // 处理卡片点击
  const handleCardClick = async (point) => {
    const info = await getKnowledgeInfo(point._id)
    setSelectedPoint(info);
    setModalVisible(true);
  };

  // 渲染 SVG 内容
  const renderSvgContent = (content) => {
    if (!content) return null;

    // 如果内容是 SVG 标签
    if (content.includes('<svg')) {
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }

    // 普通文本内容
    return <Paragraph>{content}</Paragraph>;
  };

  // 渲染知识点卡片
  const renderKnowledgePoints = (points) => {
    // 计算当前页的数据
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPagePoints = points.slice(startIndex, endIndex);

    return (
      <>
        <Row>
          {currentPagePoints.map((point, pointIndex) => (
            <div key={point._id || pointIndex}>
              <Card
                hoverable
                className={styles.knowledgeCard}
                onClick={() => handleCardClick(point)}
              >
                <div className={styles.cardContent}>
                  <Title level={4}>{point.description}</Title>
                </div>
              </Card>
            </div>
          ))}
        </Row>
        {points.length > pageSize && (
          <div className={styles.paginationWrapper}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={points.length}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
              showQuickJumper
              className={styles.pagination}
            />
          </div>
        )}
      </>
    );
  };

  const handleTabChange = (key) => {
    setActiveKey(key);
    setSelectedPoint(null); // 切换 Tab 时重置选中的知识点
    setCurrentPage(1)
  };

  // 生成 Tab 项
  const tabItems = books.map((book, index) => ({
    key: String(index),
    label: (
      <span className={styles.tabLabel}>
        
        <span>{book.book_title}</span>
      </span>
    ),
    children: (
      <div className={styles.bookSection}>
        {renderKnowledgePoints(book.knowledge_points)}
      </div>
    )
  }));

  // 获取当前知识点在当前书籍中的索引
  const getCurrentPointIndex = useCallback(() => {
    const currentBook = books[activeKey];
    if (!selectedPoint || !currentBook || !currentBook.knowledge_points) return -1;
    return currentBook.knowledge_points.findIndex(
      point => point._id === selectedPoint._id
    );
  }, [books, activeKey, selectedPoint]);

  // 获取当前是否可以切换
  const getNavigationState = () => {
    const currentIndex = getCurrentPointIndex();
    const currentBook = books[activeKey];
    if (!currentBook || !currentBook.knowledge_points) {
      return {
        canPrev: false,
        canNext: false
      };
    }
    return {
      canPrev: currentIndex > 0,
      canNext: currentIndex < currentBook.knowledge_points.length - 1
    };
  };

  // 切换到上一个知识点
  const handlePrevPoint = useCallback( async () => {
    const currentIndex = getCurrentPointIndex();
    const currentBook = books[activeKey];
    if (currentBook && currentIndex > 0) {
      const info = await getKnowledgeInfo(currentBook.knowledge_points[currentIndex - 1]._id)
      setSelectedPoint(info);
    }
  }, [books, activeKey, getCurrentPointIndex]);

  // 切换到下一个知识点
  const handleNextPoint = useCallback(async () => {
    const currentIndex = getCurrentPointIndex();
    const currentBook = books[activeKey];
    if (currentBook && currentIndex < currentBook.knowledge_points.length - 1) {
      const info = await getKnowledgeInfo(currentBook.knowledge_points[currentIndex + 1]._id)
      setSelectedPoint(info);
    }
  }, [books, activeKey, getCurrentPointIndex]);

  // 处理键盘事件
  const handleKeyPress = useCallback((event) => {
    if (!modalVisible) return; // 只在弹窗打开时处理键盘事件

    switch (event.key) {
      case 'ArrowLeft':
        handlePrevPoint();
        break;
      case 'ArrowRight':
        handleNextPoint();
        break;
      default:
        break;
    }
  }, [modalVisible, handlePrevPoint, handleNextPoint]); // 添加所有依赖项

  // 添加和移除键盘事件监听器
  useEffect(() => {
    if (modalVisible) {
      window.addEventListener('keydown', handleKeyPress);

      // 清理函数
      return () => {
        window.removeEventListener('keydown', handleKeyPress);
      };
    }
  }, [modalVisible, handleKeyPress]);

  // 添加处理函数
  const handleEnterExam = () => {
    navigate('/cate');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // 处理弹窗关闭
  const handleModalClose = () => {
    setModalVisible(false);
    setIsFullscreen(false); // 重置全屏状态
  };

  return (
    <div className={`${styles.bookContainer} ${modalVisible ? styles.modalOpen : ''}`}>
      {/* 页面头部区域 */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.titleSection}>
            <Title level={3}>
              <span className={styles.categoryPath}>
                {categoryInfo.categoryName} / {categoryInfo.subCategoryName}
              </span>
            </Title>
            <Text type="secondary">
              系统化梳理知识要点，助力高效备考
            </Text>
          </div>
          <div className={styles.statsSection}>
            {/* 单独放置二维码，不使用statItem样式 */}
            <div className={styles.qrCodeWrapper}>
              <img 
                src={qrCodeImage} 
                alt="小程序二维码" 
                className={styles.qrCodeImage} 
              />
              <div className={styles.qrCodeTooltip}>扫码查看更多知识点</div>
            </div>
            <div className={styles.statItem}>
              <Title level={4}>{books.reduce((acc, book) => acc + book.knowledge_points.length, 0)}</Title>
              <Text type="secondary">图解知识</Text>
            </div>



            {/* 添加进入试题按钮 */}
            <Button
              onClick={handleEnterExam}
              className={styles.examButton}
            >
              <BookOutlined />
              <span>进入试题</span>
            </Button>
            
            {/* 查看笔记按钮 */}
            <Button
              onClick={openNotesPage}
              className={styles.notesButton}
            >
              <FileTextOutlined />
              <span>查看笔记</span>
            </Button>
          </div>
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className={styles.knowledgePointsSection}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        ) : books.length === 0 ? (
          <Empty
            description="暂无知识点数据"
            image={Empty.PRESENTED_IMAGE_DARK}
          />
        ) : (
          <Tabs
            activeKey={activeKey}
            onChange={handleTabChange}
            items={tabItems}
            className={styles.bookTabs}
            type="card"
          />
        )}
      </div>

      {/* 底部固定的优雅按钮 */}
      <div className={styles.footerButtonWrapper}>
        <Button
          type="primary"
          size="large"
          icon={<ArrowRightOutlined />}
          className={styles.footerActionButton}
          onClick={openNotesPage}
        >
          前往完整的考点笔记
        </Button>
      </div>

      {/* 知识点详情弹窗 */}
      <Modal
        title={
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>
              <BookOutlined className={styles.modalIcon} />
              <span>{selectedPoint?.description}</span>
            </div>
            <div className={styles.modalActions}>
              <div className={styles.modalTip}>
                键盘← →翻页
              </div>
              <button
                className={styles.fullscreenButton}
                onClick={toggleFullscreen}
                title={isFullscreen ? "退出全屏" : "全屏显示"}
              >
                {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              </button>
            </div>
          </div>
        }
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        className={isFullscreen ? 'fullscreen-modal' : styles.detailModal}
        closeIcon={null}
      >
        <div className={styles.modalContent}>
          {selectedPoint && renderSvgContent(selectedPoint.description_more)}
        </div>
        <div className={styles.modalNavigation}>
          <button
            className={`${styles.navButton} ${!getNavigationState().canPrev ? styles.disabled : ''}`}
            onClick={handlePrevPoint}
            disabled={!getNavigationState().canPrev}
          >
            <LeftOutlined />
            <span>上一个</span>
          </button>
          <div className={styles.navigationInfo}>
            {books[activeKey]?.knowledge_points ?
              `${getCurrentPointIndex() + 1} / ${books[activeKey].knowledge_points.length}` :
              '0 / 0'
            }
          </div>
          <button
            className={`${styles.navButton} ${!getNavigationState().canNext ? styles.disabled : ''}`}
            onClick={handleNextPoint}
            disabled={!getNavigationState().canNext}
          >
            <span>下一个</span>
            <RightOutlined />
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default StudyBook;